import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentHistory } from 'data/reducers/subscriptionReducer';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import './PaymentHistory.css';
import moment from 'moment/moment';

const PaymentHistory = () => {
	const dispatch = useDispatch();
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(15);

	const { paymentHistory, isLoadingPaymentHistory, paymentHistoryError } =
		useSelector((state) => state.subscription);

	useEffect(() => {
		dispatch(fetchPaymentHistory(currentPage, perPage));
	}, [dispatch, currentPage, perPage]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	const handlePerPageChange = (newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1); // Reset to first page when changing per page
	};

	const getStatusBadgeClass = (status) => {
		switch (status.toLowerCase()) {
			case 'succeeded':
				return 'badge-success';
			case 'failed':
				return 'badge-danger';
			case 'pending':
				return 'badge-warning';
			case 'requires_action':
				return 'badge-info';
			case 'canceled':
				return 'badge-secondary';
			default:
				return 'badge-secondary';
		}
	};

	const getTypeDisplay = (type) => {
		switch (type.toLowerCase()) {
			case 'payment':
				return 'Payment';
			case 'refund':
				return 'Refund';
			case 'failed_payment':
				return 'Failed Payment';
			case 'partial_refund':
				return 'Partial Refund';
			default:
				return type.charAt(0).toUpperCase() + type.slice(1);
		}
	};

	if (isLoadingPaymentHistory) {
		return (
			<div className="payment-history-container">
				<div className="payment-history-header">
					<h3>Payment History</h3>
				</div>
				<div className="text-center py-5">
					<div className="spinner-border" role="status">
						<span className="sr-only">Loading payment history...</span>
					</div>
				</div>
			</div>
		);
	}

	if (paymentHistoryError) {
		return (
			<div className="payment-history-container">
				<div className="payment-history-header">
					<h3>Payment History</h3>
				</div>
				<div className="alert alert-danger">
					Error loading payment history: {paymentHistoryError}
				</div>
			</div>
		);
	}

	return (
		<div className="payment-history-container">
			<div className="payment-history-header">
				<h3>Payment History</h3>
				<div className="payment-history-controls">
					<label className="per-page-label">
						Show:
						<select
							value={perPage}
							onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
							className="form-control form-control-sm ml-2"
						>
							<option value={10}>10</option>
							<option value={15}>15</option>
							<option value={25}>25</option>
							<option value={50}>50</option>
						</select>
						entries
					</label>
				</div>
			</div>

			{paymentHistory &&
			paymentHistory.data &&
			paymentHistory.data.length > 0 ? (
				<>
					<div className="table-responsive">
						<table className="table table-striped payment-history-table">
							<thead>
								<tr>
									<th scope="col">Date</th>
									<th scope="col">Type</th>
									<th scope="col">Description</th>
									<th scope="col">Amount</th>
									<th scope="col">Status</th>
									<th scope="col">Transaction ID</th>
								</tr>
							</thead>
							<tbody>
								{paymentHistory.data.map((transaction) => (
									<tr key={transaction.id}>
										<td>
											<div className="transaction-date">
												{moment(transaction.created_at).format(
													'MM-DD-YYYY, HH:mm'
												)}
											</div>
										</td>
										<td>
											<span className="transaction-type">
												{getTypeDisplay(transaction.type)}
											</span>
										</td>
										<td>
											<div className="transaction-description">
												{transaction.description || 'No description'}
											</div>
										</td>
										<td>
											<div className="transaction-amount">
												{transaction.amount_formatted}
											</div>
										</td>
										<td>
											<span
												className={`badge ${getStatusBadgeClass(
													transaction.status
												)}`}
											>
												{transaction.status.charAt(0).toUpperCase() +
													transaction.status.slice(1)}
											</span>
										</td>
										<td>
											<div className="transaction-id">
												<small className="text-muted">
													{transaction.stripe_charge_id
														? `${transaction.stripe_charge_id}`
														: `tx_${transaction.id}`}
												</small>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="payment-history-footer">
						<div className="payment-history-info">
							Showing {paymentHistory.from} to {paymentHistory.to} of{' '}
							{paymentHistory.total} entries
						</div>

						{/* Same pagination as the rest of the v3 dashboard */}
						{paymentHistory.lastPage > 1 && (
							<PaginatorV3
								currentPage={paymentHistory.currentPage}
								totalPages={paymentHistory.lastPage}
								onPageChange={handlePageChange}
							/>
						)}
					</div>
				</>
			) : (
				<div className="no-payment-history">
					<div className="text-center py-5">
						<i className="fa fa-credit-card fa-3x text-muted mb-3"></i>
						<h5>No Payment History</h5>
						<p className="text-muted">
							You don't have any payment transactions yet.
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default PaymentHistory;
